import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import SeoHead from '@/components/base/SeoHead';
import HeroBanner from './components/HeroBanner';
import { tvShows, categories, rankings } from '@/mocks/tvShows';

function getShowById(id: number) {
  return tvShows.find(s => s.id === id);
}

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');
  const filteredShows = activeCategory === 'all'
    ? tvShows
    : tvShows.filter(s => s.categorySlug === activeCategory);
  const latestShows = [...tvShows].sort((a, b) => b.year - a.year).slice(0, 8);
  const hotRankShows = rankings.hot.map(id => getShowById(id)).filter(Boolean);
  const topRatedShows = rankings.rating.map(id => getShowById(id)).filter(Boolean);

  const homepageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '97影院',
    url: 'https://97yingyuan.com',
    description: '97影院提供电视剧全集在线观看，热门国产剧、韩剧、美剧全集免费看，高清蓝光不卡顿流畅播放',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://97yingyuan.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '97影院',
    url: 'https://97yingyuan.com',
    description: '高清蓝光不卡顿，热门电视剧全集免费在线观看平台',
  };

  return (
    <>
      <SeoHead
        title="97影院 - 电视剧全集在线观看，热门电视剧最新国产剧韩剧美剧全集免费看，高清蓝光不卡顿流畅播放"
        description="97影院提供电视剧全集在线观看，热门国产剧、韩剧、美剧全集免费看，最新剧集更新快，高清蓝光不卡顿，流畅播放。"
        keywords="电视剧全集,全集在线观看,热门电视剧,国产剧全集,韩剧全集,美剧全集,最新电视剧,免费在线观看,高清,97影院"
        canonicalUrl="https://97yingyuan.com"
        ogImage={tvShows[0].poster}
        schemaData={{ ...homepageSchema }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema, null, 2) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <HeroBanner />

          <section className="w-full px-4 md:px-6 py-8 md:py-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  热门分类
                </h2>
                <Link
                  to="/category/guochanju"
                  className="text-sm text-foreground-400 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看全部 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 md:gap-4">
                {categories.filter(c => c.slug !== 'all').map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group relative overflow-hidden rounded-xl aspect-[16/10] bg-background-100 cursor-pointer"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-primary-500/20 to-accent-500/20 group-hover:from-primary-500/40 group-hover:to-accent-500/40 transition-colors duration-300"></div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                      <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-50/80">
                        <i className="ri-movie-2-line text-xl md:text-2xl text-primary-500"></i>
                      </div>
                      <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{cat.name}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-100/50">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  精选推荐
                </h2>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {categories.map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => setActiveCategory(cat.slug)}
                      className={`px-3 py-1.5 text-sm rounded-full transition-all cursor-pointer whitespace-nowrap ${
                        activeCategory === cat.slug
                          ? 'bg-primary-500 text-background-50 font-medium'
                          : 'bg-background-100 text-foreground-500 hover:text-foreground-800 hover:bg-background-200'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {filteredShows.slice(0, 12).map((show) => (
                  <MovieCard key={show.id} show={show} />
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-8 md:py-10">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                      <i className="ri-fire-line text-accent-500 mr-2"></i>
                      热播排行榜
                    </h2>
                    <Link
                      to="/category/all"
                      className="text-sm text-foreground-400 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      查看更多 <i className="ri-arrow-right-line ml-1"></i>
                    </Link>
                  </div>
                  <div className="space-y-3">
                    {hotRankShows.slice(0, 8).map((show, index) => show && (
                      <Link
                        key={show.id}
                        to={`/detail/${show.id}`}
                        className="flex items-center gap-4 p-3 rounded-xl bg-background-100 hover:bg-background-200 transition-colors cursor-pointer group"
                      >
                        <span className={`w-7 h-7 flex items-center justify-center rounded-lg font-heading font-bold text-sm whitespace-nowrap ${
                          index < 3 ? 'bg-accent-500 text-white' : 'bg-background-200 text-foreground-400'
                        }`}>
                          {index + 1}
                        </span>
                        <div className="w-12 h-16 rounded-lg overflow-hidden flex-shrink-0">
                          <img src={show.poster} alt={show.name} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                            {show.name}
                          </h3>
                          <p className="text-xs text-foreground-400 mt-1">{show.category} · {show.year}</p>
                        </div>
                        <span className="text-sm font-semibold text-primary-400 whitespace-nowrap">{show.rating}分</span>
                      </Link>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                      <i className="ri-star-line text-primary-500 mr-2"></i>
                      高分榜单
                    </h2>
                    <Link
                      to="/category/all"
                      className="text-sm text-foreground-400 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      查看更多 <i className="ri-arrow-right-line ml-1"></i>
                    </Link>
                  </div>
                  <div className="space-y-3">
                    {topRatedShows.slice(0, 8).map((show, index) => show && (
                      <Link
                        key={show.id}
                        to={`/detail/${show.id}`}
                        className="flex items-center gap-4 p-3 rounded-xl bg-background-100 hover:bg-background-200 transition-colors cursor-pointer group"
                      >
                        <span className={`w-7 h-7 flex items-center justify-center rounded-lg font-heading font-bold text-sm whitespace-nowrap ${
                          index < 3 ? 'bg-primary-500 text-white' : 'bg-background-200 text-foreground-400'
                        }`}>
                          {index + 1}
                        </span>
                        <div className="w-12 h-16 rounded-lg overflow-hidden flex-shrink-0">
                          <img src={show.poster} alt={show.name} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                            {show.name}
                          </h3>
                          <p className="text-xs text-foreground-400 mt-1">{show.category} · {show.year}</p>
                        </div>
                        <span className="text-sm font-semibold text-primary-400 whitespace-nowrap">{show.rating}分</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-100/50">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  最新更新
                </h2>
                <Link
                  to="/category/all"
                  className="text-sm text-foreground-400 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看全部 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4 md:gap-5">
                {latestShows.map((show) => (
                  <MovieCard key={show.id} show={show} />
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-7xl mx-auto">
              <div className="bg-background-100 rounded-2xl p-8 md:p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-2xl bg-primary-500/20">
                  <i className="ri-tv-2-line text-3xl text-primary-500"></i>
                </div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  海量影视资源，尽在97影院
                </h2>
                <p className="text-foreground-400 max-w-lg mx-auto mb-6 text-sm leading-relaxed">
                  涵盖国产剧、韩剧、美剧、日剧、泰剧、动漫等各类影视资源，每日更新最新剧集。高清蓝光画质，流畅不卡顿，带给你极致的观影体验。
                </p>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 font-semibold text-sm rounded-xl hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  了解更多
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}